import { useMemo } from "react"
import { useQuery, type UseQueryResult } from "@tanstack/react-query"
import { constants } from "helpers"
import type { SxSummarySport, SxSummaryFixture } from "../types/markets"
import {
  transformSxActiveMarkets,
  transformSxMarketsForComponents,
} from "../utils/transformSxMarkets"
import type { SxActiveMarketsParams } from "./types"
import { SX_CONFIG } from "../constants"
import useChainProviderHook from "@/hooks/useChainProvider"
import { useSxSportsNavigation } from "./useSxSportsNavigation"

/**
 * Fetch league summary data for a specific league and bet group
 */
// Response interface for league summary endpoint
interface SxLeagueSummaryResponse {
  status: string
  data: {
    scope: "league"
    sportId: number
    leagueId: number
    label: string
    eventsByType: Record<string, number>
    days: Array<{
      day: string
      marketTypes: string[]
      fixtures: SxSummaryFixture[]
    }>
  }
}

const fetchLeagueSummary = async (
  chainId: number,
  leagueId: number,
  betGroup: string = "1X2"
): Promise<SxLeagueSummaryResponse> => {
  const TOKEN = SX_CONFIG[chainId].CONTRACTS.TOKEN
  const url = `${constants.getSxBetApiUrl()}/summary/league/${TOKEN}/${leagueId}/${betGroup}?chainId=${chainId}`

  const response = await fetch(url)
  if (!response.ok) {
    throw new Error(`HTTP error! status: ${response.status}`)
  }

  const result: SxLeagueSummaryResponse = await response.json()
  if (result.status !== "success") {
    throw new Error(`API error: ${result.status}`)
  }

  return result
}

/**
 * Hook for specific sports
 * Fetches markets for specified sport by collating all league data for that sport
 * Uses the same pattern as useSxLeagueMarkets for consistent behavior
 */
export const useSxSportMarket = (
  sportId: number,
  params?: Omit<SxActiveMarketsParams, "sportIds" | "enabled"> & {
    enabled?: boolean
  }
): UseQueryResult<{
  sports: any[]
  markets: any[]
}> => {
  const { chainId, isSXChain } = useChainProviderHook()
  const { data: navData } = useSxSportsNavigation()
  const { leagues } = navData || {}

  // Get all leagues for this sport and their bet groups
  const sportLeagues = useMemo(() => {
    if (!leagues) {
      return []
    }
    return leagues.filter((league: any) => league.sportId === sportId)
  }, [leagues, sportId])

  // Get all bet group combinations for all leagues
  const leagueBetGroups = useMemo(() => {
    return sportLeagues.flatMap((league: any) => {
      const betGroups = league.eventsByType
        ? Object.keys(league.eventsByType)
        : ["1X2"]
      return betGroups.map(betGroup => ({
        leagueId: league.leagueId,
        betGroup,
        leagueLabel: league.label,
      }))
    })
  }, [sportLeagues])

  // Fetch data for all league/bet group combinations in parallel (like useSxLeagueMarkets)
  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ["sx-sport-market-leagues", sportId, chainId, leagueBetGroups],
    queryFn: async () => {
      if (leagueBetGroups.length === 0) {
        return { sports: [] }
      }

      // Fetch all league data in parallel
      const responses = await Promise.all(
        leagueBetGroups.map(({ leagueId, betGroup }) =>
          fetchLeagueSummary(chainId, leagueId, betGroup).catch(error => {
            console.warn(
              `Failed to fetch league ${leagueId} betGroup ${betGroup}:`,
              error
            )
            return null
          })
        )
      )

      const validResponses = responses.filter(response => response !== null)

      if (validResponses.length === 0) {
        return { sports: [] }
      }

      // Collate all fixtures from all leagues into a single sport structure
      const allFixtures: any[] = []
      const allMarketTypes = new Set<string>()

      validResponses.forEach(response => {
        if (response?.data?.days) {
          response.data.days.forEach((day: any) => {
            if (day.fixtures) {
              allFixtures.push(...day.fixtures)
            }
            if (day.marketTypes) {
              day.marketTypes.forEach((type: string) =>
                allMarketTypes.add(type)
              )
            }
          })
        }
      })

      // Group fixtures by league
      const leagueMap = new Map<number, any>()

      allFixtures.forEach(fixture => {
        const leagueId = fixture.leagueId
        if (!leagueMap.has(leagueId)) {
          const leagueInfo = sportLeagues.find(
            (l: any) => l.leagueId === leagueId
          )
          leagueMap.set(leagueId, {
            leagueId,
            leagueLabel: leagueInfo?.label || `League ${leagueId}`,
            marketTypes: Array.from(allMarketTypes),
            fixtures: [],
          })
        }
        leagueMap.get(leagueId).fixtures.push(fixture)
      })

      // Create sport structure
      const sport: SxSummarySport = {
        sportId,
        leagues: Array.from(leagueMap.values()),
      }

      return { sports: [sport] }
    },
    enabled:
      params?.enabled !== false &&
      !!chainId &&
      !!sportId &&
      leagueBetGroups.length > 0,
    gcTime: 1000 * 30, // 30 seconds
    staleTime: 1000 * 30, // 30 seconds
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  })

  // Transform the collated league data into the expected format
  const result = useMemo(() => {
    if (!data?.sports || data.sports.length === 0) {
      return { sports: [], markets: [] }
    }

    const sports = transformSxActiveMarkets(data.sports, chainId)
    // Transform markets for SXOutcomeButton compatibility
    const transformedMarkets = transformSxMarketsForComponents(
      data.sports,
      chainId,
      isSXChain
    )

    return { sports, markets: transformedMarkets }
  }, [data, chainId, isSXChain])

  return {
    data: result,
    isLoading,
    isFetching,
    isPending: isLoading,
    isError: !!error,
    error: error as Error | undefined,
  } as UseQueryResult<{ sports: any[]; markets: any[] }>
}
